import styled from "styled-components";

// Scrollable in both directions (unlike the shared TableWrapperStyled, which
// only scrolls vertically) so a wide/tall org chart can be panned to before
// or instead of zooming out. Also click-and-drag pannable (see
// SuccessionPlanningV2.jsx's pointer handlers) and Ctrl/Cmd+scroll zoomable,
// mirroring the pan/zoom feel of dedicated diagramming tools — grab cursor at
// rest, grabbing while actively dragging.
export const ChartScrollAreaStyled = styled.div`
  flex-grow: 1;
  overflow: auto;
  position: relative;
  /* Matches TreeContainerStyled's own background so shrinking it (zoom out)
     never reveals the page's own background color around it. */
  background: #fff;
  cursor: ${(p) => (p.$isPanning ? "grabbing" : "grab")};
  user-select: ${(p) => (p.$isPanning ? "none" : "auto")};
`;

// transform-origin: top center — zooming in/out scales from the chart's own
// horizontal center, so it always stays centered in the viewport at any zoom
// level instead of drifting toward the top-left corner.
export const TreeContainerStyled = styled.div`
  padding: 32px 24px;
  background: #fff;
  min-width: fit-content;
  transform: scale(${(p) => p.$zoom ?? 1});
  transform-origin: top center;
  transition: transform 0.15s ease;
`;

export const ZoomControlsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ZoomLevelStyled = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  min-width: 38px;
  text-align: center;
`;

export const FilterRowStyled = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 24px;
  border-bottom: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#EEF1F5"};

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 180px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  }
`;

export const FilterActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

// Shared centered container for loading/error/empty states over the chart
// viewport, so the header/filter row above stays visible and interactive.
export const CenteredStateStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 320px;
  padding: 48px 24px;
  text-align: center;
`;

export const StateMessageStyled = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

const CONNECTOR = "#D8DAE5";
const CONNECTOR_GAP = 28; // px between a box and the row of its children

// Classic centered, symmetric org-chart layout (box + connector lines
// branching both left and right to children), built in pure CSS via nested
// <ul>/<li> — no charting library needed. Each `li` draws two half-width
// top borders that, combined with its siblings, form one continuous
// horizontal bar; the child `<ul>`'s own top border is the vertical stub
// connecting a parent box down to that bar. Single children get just the
// vertical stub (no bar, since there's nothing to branch to).
export const OrgChartListStyled = styled.ul`
  display: flex;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 0;

  ul {
    display: flex;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding-top: ${CONNECTOR_GAP}px;
    position: relative;
  }

  li {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding: ${CONNECTOR_GAP}px 12px 0;
  }

  li::before,
  li::after {
    content: "";
    position: absolute;
    top: 0;
    width: 50%;
    height: ${CONNECTOR_GAP}px;
    border-top: 1px solid ${CONNECTOR};
  }
  li::before {
    right: 50%;
  }
  li::after {
    left: 50%;
    border-left: 1px solid ${CONNECTOR};
  }

  li:only-child {
    padding-top: 0;
  }
  li:only-child::before,
  li:only-child::after {
    display: none;
  }

  li:first-child::before,
  li:last-child::after {
    border: none;
  }
  li:last-child::before {
    border-right: 1px solid ${CONNECTOR};
    border-radius: 0 6px 0 0;
  }
  li:first-child::after {
    border-radius: 6px 0 0 0;
  }

  /* vertical stub from a parent box down to its children's horizontal bar */
  ul::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 0;
    height: ${CONNECTOR_GAP}px;
    border-left: 1px solid ${CONNECTOR};
  }

  /* root level — no incoming connector */
  & > li {
    padding-top: 0;
  }
  & > li::before,
  & > li::after {
    display: none;
  }
`;

// ─── Employee / position card ─────────────────────────────────────────────────

// role="button" is only set when a position has more than one occupant (see
// SuccessionEmployeeCard) — the &[role="button"] selector styles just that
// case as clickable, without needing a separate transient prop for it.
export const EmployeeCardStyled = styled.div`
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 10px;
  background: ${(p) => (p.$contextOnly ? p.theme?.colors?.light ?? "#F7F7F7" : "#fff")};
  padding: 7px 14px;
  margin: 3px 0;
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  opacity: ${(p) => (p.$contextOnly ? 0.75 : 1)};

  &[role="button"] {
    cursor: pointer;
  }
  &[role="button"]:hover,
  &[role="button"]:focus-visible {
    border-color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  }
`;

export const CardHeaderRowStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const CardIdentityStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

// Position/role title — the card's primary heading.
export const CardRoleTitleStyled = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Currently-selected occupant's name — secondary to the role title.
export const CardEmployeeNameStyled = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Tenure suffix ("· 24 mo") — a separate inline element from the employee
// name on purpose, so the name text itself stays queryable/selectable on its
// own (e.g. by an exact-text lookup) instead of being merged into one string.
export const EmployeeTenureStyled = styled.span`
  font-weight: 400;
  opacity: 0.85;
`;

// Groups the employee name + caret when a position has more than one
// occupant. Purely visual — the whole card (not just this row) is the click
// target that opens the picker below it, see EmployeeCardStyled's
// &[role="button"] styling.
export const CardNameRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
`;

// The multi-occupant picker — a connected panel just under the card (a
// sibling in the DOM, not nested inside it), indented with a left border so
// it visually reads the same way the old mockup's "Top potential successors"
// panel did, just listing real occupants instead of a fake score.
export const InlineEmployeePickerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 0 4px 16px;
  padding: 8px 10px;
  border-left: 2px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  background: ${(p) => p.theme?.colors?.light ?? "#F7F7F7"};
  border-radius: 0 6px 6px 0;
  width: 220px;
`;

export const InlineEmployeePickerTitleStyled = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  margin-bottom: 4px;
`;

export const InlineEmployeeOptionStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 11.5px;
  font-weight: ${(p) => (p.$selected ? 700 : 500)};
  color: ${(p) => (p.$selected ? p.theme?.colors?.primary ?? "#008080" : p.theme?.colors?.dark ?? "#2B3034")};
  background: ${(p) => (p.$selected ? p.theme?.colors?.hoverBg ?? "rgba(0, 128, 128, 0.1)" : "transparent")};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme?.colors?.hoverBg ?? "rgba(0, 128, 128, 0.1)"};
  }
`;

export const ToggleButtonStyled = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 6px;
  background: #fff;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  font-size: 10px;
  cursor: pointer;

  &:hover {
    border-color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  }
`;

export const ContextBadgeStyled = styled.div`
  align-self: flex-start;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  background: ${(p) => p.theme?.colors?.secondary ?? "#DDEAEA"};
  border-radius: 4px;
  padding: 2px 6px;
`;

export const CardMetaGridStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CardMetaRowStyled = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
`;

export const CardMetaLabelStyled = styled.span`
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  flex-shrink: 0;
`;

export const CardMetaValueStyled = styled.span`
  color: ${(p) => p.theme?.colors?.dark ?? "#2B3034"};
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

// ─── Score section (readiness ring, Ready for Role, Matches, Succession Score) ─
// Every value here may be a real API field or the shared static placeholder
// (see successionScorePlaceholder.constants.js) — this file only styles the
// section, PlaceholderBadgeStyled is what flags placeholder data to the user.

export const GaugeStyled = styled.div`
  flex-shrink: 0;
  text-align: center;
  .ant-progress-text {
    font-size: 9px !important;
    font-weight: 700;
  }
`;

export const PlaceholderBadgeStyled = styled.span`
  align-self: flex-start;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme?.colors?.dark ?? "#2B3034"};
  background: repeating-linear-gradient(
    135deg,
    #fff7e6,
    #fff7e6 4px,
    #ffedc2 4px,
    #ffedc2 8px
  );
  border: 1px dashed #d9a441;
  border-radius: 4px;
  padding: 1px 6px;
  cursor: help;
`;

export const SuccessionRowStyled = styled(CardMetaRowStyled)`
  gap: 10px;
`;

export const SuccessionBarWrapStyled = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SuccessionValueStyled = styled.span`
  font-size: 13px;
  font-weight: 800;
  flex-shrink: 0;
  min-width: 26px;
  text-align: right;
`;

// ─── "Top potential successors" — always the static empty state, never a ─────
// real or fake employee name (see successionScorePlaceholder.constants.js /
// SuccessionEmployeeCard.jsx) — no successor-matching data exists yet.

export const SuccessorsToggleStyled = styled.button`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 700;
  color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  cursor: pointer;
`;

export const SuccessorsPanelStyled = styled.div`
  margin: 2px 0 0 0;
  padding: 6px 10px;
  border-left: 2px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  font-size: 11.5px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

// ─── "Reading This Chart" legend ───────────────────────────────────────────────
// Pinned to the top-right corner of the chart's scroll viewport (sticky on
// both axes, since ChartScrollAreaStyled scrolls both ways) rather than
// placed in normal flow — the org chart's own content position shifts with
// zoom/scroll, but this stays put in the empty corner regardless.
export const IndexLegendCardStyled = styled.div`
  position: sticky;
  top: 16px;
  right: 16px;
  float: right;
  z-index: 5;
  width: 260px;
  background: #fff;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
    margin-bottom: 6px;
  }
`;
